import type { MetricKey } from '../lib/mapMetric';
import type { Point, Reading } from '../types';

const BASE = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter';
const APP = 'sfbaywind';

/**
 * CO-OPS `units=english` returns wind in KNOTS, not mph — verified against the
 * metric response (19.44 kt / 10.0 m/s = 1.944). Values pass through unconverted.
 *
 * Times are requested in GMT so they parse unambiguously; `lst_ldt` would return
 * a local wall-clock string with no offset.
 */
interface CoopsWindRow {
  t: string;
  s: string;
  d: string;
  dr: string;
  g: string;
}

function parseUtc(t: string): Date {
  return new Date(`${t.replace(' ', 'T')}:00Z`);
}

/**
 * CO-OPS uses an empty string for a missing sample, and `Number('')` is 0 — a
 * value that passes `Number.isFinite` and plots as a real reading. Left
 * unguarded it drew 0 °F spikes into the temperature panel and dragged the axis
 * down to -15. Every numeric field must come through here.
 */
export function num(v: string | undefined | null): number | null {
  if (v === undefined || v === null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function toReading(row: CoopsWindRow): Reading | null {
  const speedKt = num(row.s);
  if (speedKt === null) return null;
  return {
    t: parseUtc(row.t),
    speedKt,
    dirDeg: num(row.d),
    gustKt: num(row.g),
  };
}

/**
 * One paced retry on the statuses CO-OPS uses to shed load.
 *
 * A refresh asks for fourteen stations at once, which is the hardest burst this
 * app makes, and CO-OPS answers some of them with 403, 502 or 504 rather than
 * queueing. Without a retry a single shed request blanks that station until the
 * next refresh six minutes later — which is what "many stations not showing
 * data" looks like from outside.
 *
 * The same guard has been in tides.ts and currents.ts since they were written;
 * this module simply never got it, despite bursting harder than either.
 *
 * Deliberately not retried: a 4xx that is not 403 or 429 (a bad station id will
 * not fix itself) and the error object CO-OPS returns with HTTP 200, which is a
 * real "no data for this sensor" rather than a refusal to serve.
 */
interface CoopsBody {
  error?: { message?: string };
  data?: unknown;
  predictions?: unknown;
}

async function getJson(url: string, label: string, attempt = 0): Promise<CoopsBody> {
  const res = await fetch(url);

  if (!res.ok) {
    if (attempt === 0 && (res.status === 403 || res.status === 429 || res.status >= 500)) {
      await new Promise((r) => setTimeout(r, 900));
      return getJson(url, label, attempt + 1);
    }
    throw new Error(`${label} HTTP ${res.status}`);
  }

  return res.json();
}

async function fetchWind(params: Record<string, string>): Promise<Reading[]> {
  const qs = new URLSearchParams({
    product: 'wind',
    units: 'english',
    time_zone: 'gmt',
    format: 'json',
    application: APP,
    ...params,
  });

  const body = await getJson(`${BASE}?${qs}`, 'CO-OPS');
  // CO-OPS signals "no data" with HTTP 200 and an error object, not a status code.
  if (body.error) throw new Error(body.error.message ?? 'CO-OPS returned no data');
  if (!Array.isArray(body.data)) throw new Error('CO-OPS returned no data array');

  return (body.data as CoopsWindRow[])
    .map(toReading)
    .filter((r): r is Reading => r !== null)
    .sort((a, b) => a.t.getTime() - b.t.getTime());
}

export async function latest(stationId: string): Promise<Reading> {
  const rows = await fetchWind({ date: 'latest', station: stationId });
  if (rows.length === 0) throw new Error('No current observation');
  return rows[rows.length - 1];
}

export async function history(stationId: string, hours = 24): Promise<Reading[]> {
  return fetchWind({ range: String(hours), station: stationId });
}

/**
 * Scalar products (water level, temperatures, tide predictions) all come back as
 * `{t, v}` rows, unlike wind's `{t, s, d, g}`.
 *
 * `water_level` and `predictions` additionally require a datum — without it
 * CO-OPS reports the product as unavailable even at stations that have it.
 */
async function fetchScalar(
  stationId: string,
  product: string,
  hours: number,
  extra: Record<string, string> = {},
): Promise<Point[]> {
  const qs = new URLSearchParams({
    product,
    range: String(hours),
    units: 'english',
    time_zone: 'gmt',
    format: 'json',
    application: APP,
    station: stationId,
    ...extra,
  });

  const body = await getJson(`${BASE}?${qs}`, `CO-OPS ${product}`);
  if (body.error) throw new Error(body.error.message ?? `No ${product} data`);

  const rows = body.data ?? body.predictions;
  if (!Array.isArray(rows)) throw new Error(`No ${product} data`);

  return rows
    .map((r: { t: string; v: string }) => ({ t: parseUtc(r.t), v: num(r.v) }))
    .filter((p: { t: Date; v: number | null }): p is Point => p.v !== null)
    .sort((a: Point, b: Point) => a.t.getTime() - b.t.getTime());
}

/** Observed water level, feet above MLLW. */
export const waterLevel = (id: string, hours = 24) =>
  fetchScalar(id, 'water_level', hours, { datum: 'MLLW' });

/** Predicted tide over the same window, for comparison against observed. */
export const waterLevelPredicted = (id: string, hours = 24) =>
  fetchScalar(id, 'predictions', hours, { datum: 'MLLW', interval: '6' });

/** Water temperature in °F — `units=english` applies here too. */
export const waterTemp = (id: string, hours = 24) =>
  fetchScalar(id, 'water_temperature', hours);

export const airTemp = (id: string, hours = 24) =>
  fetchScalar(id, 'air_temperature', hours);

/**
 * The most recent value of one metric, for the map's labels.
 *
 * A short window rather than the chart's 24 hours: only the last point is
 * wanted, and a station reporting every six minutes needs no more than a couple
 * of hours to have one. Returns null when the sensor is listed but silent.
 */
export async function latestMetric(id: string, metric: MetricKey): Promise<number | null> {
  const fetcher = {
    waterTemp,
    airTemp,
    waterLevel,
    // CO-OPS shore stations carry no wave sensor; the filter never selects them.
    waves: null,
    // Wind is already on the station's reading and never fetched here.
    wind: null,
  }[metric];
  if (!fetcher) return null;

  const points = await fetcher(id, 2);
  return points.length > 0 ? points[points.length - 1].v : null;
}
